import { useAtomValue } from 'jotai/index';
import { selectAtom } from 'jotai/utils';
import { useMemo } from 'react';
import { InstrumentStore, InstrumentStoreItem, ListedInstrument } from '../../types';
import { InstrumentSubscriptionProps } from './useInstrumentStoreItem';
import { useInstrumentSubscription } from './useInstrumentSubscription';
import { SubscribeToInstrumentChangesFn } from './useSubscribeToInstrumentChanges';

/**
 * Atom-level selector wrapper around an instrument store atom.
 * The component only re-renders when the selected slice actually changes,
 * because selectAtom creates a derived atom that jotai only triggers on slice inequality.
 *
 * `selector` and `isEqual` MUST be stable references — define them at module level
 * whenever possible. Use `useCallback` only when closure over local state is unavoidable.
 * Unstable references will cause a new derived atom to be created on every render,
 * defeating the purpose of atom-level selection.
 *
 * @example
 * // ✅ Preferred: module-level functions
 * const selectPrice = (item: InstrumentStoreItem | null) => item?.price;
 * const priceEqual = (a?: InstrumentStorePrice, b?: InstrumentStorePrice) =>
 *   a?.value === b?.value && a?.type === b?.type;
 *
 * const price = useInstrumentStoreItemSelector(
 *   instrument, instrumentStore, subscribeToInstrumentChanges,
 *   selectPrice, priceEqual,
 *   { source: 'myComponent' },
 * );
 *
 * // ⚠️ Only when closure is needed:
 * const selectFiltered = useCallback(
 *   (item: InstrumentStoreItem | null) => item?.price?.value ?? fallback,
 *   [fallback],
 * );
 */
export const useInstrumentStoreItemSelector = <T>(
  instrument: ListedInstrument | null,
  instrumentStore: InstrumentStore,
  subscribeToInstrumentChanges: SubscribeToInstrumentChangesFn,
  selector: (item: InstrumentStoreItem | null) => T,
  isEqual?: (a: T, b: T) => boolean,
  subscriptionParams?: InstrumentSubscriptionProps,
): T => {
  const instrumentAtom = useInstrumentSubscription(
    instrument,
    instrumentStore,
    subscribeToInstrumentChanges,
    subscriptionParams,
  );

  const derivedAtom = useMemo(() => selectAtom(instrumentAtom, selector, isEqual), [instrumentAtom, selector, isEqual]);

  return useAtomValue(derivedAtom);
};
